import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { api } from '../App';
import { 
  Plus, Edit2, Trash2, Check, X, ChevronDown, ChevronUp, 
  ArrowUpDown, Filter, Building2, User, Hash, CreditCard, 
  Star, FileText, Link, Unlink, TrendingUp, TrendingDown, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const DematAccounts = ({ user, setUser }) => {
  const [holdings, setHoldings] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBroker, setExpandedBroker] = useState(null);
  
  // Filter and sort states
  const [sortConfig, setSortConfig] = useState({ key: 'broker', direction: 'asc' });
  const [filters, setFilters] = useState({
    broker: '',
    memberName: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, holdingsRes] = await Promise.all([
        api.get('/family-members'),
        api.get('/holdings'),
      ]);
      setMembers(membersRes.data);
      setHoldings(holdingsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get member name helper
  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  // Group holdings by broker (bank_name)
  const brokerData = useMemo(() => {
    const grouped = {};
    
    holdings.forEach(h => {
      const broker = h.bank_name || 'Unassigned';
      
      if (!grouped[broker]) {
        grouped[broker] = {
          broker,
          holdings: [],
          members: new Set(),
          totalQuantity: 0,
          totalInvested: 0,
          totalCurrentValue: 0,
          uniqueStocks: new Set(),
          pledgedCount: 0,
          freeCount: 0,
          unlistedCount: 0
        };
      }
      
      grouped[broker].holdings.push(h);
      grouped[broker].members.add(h.member_id);
      grouped[broker].totalQuantity += h.quantity || 0;
      grouped[broker].totalInvested += (h.quantity || 0) * (h.purchase_price || 0);
      grouped[broker].totalCurrentValue += (h.quantity || 0) * (h.custom_fields?.current_price || h.purchase_price || 0);
      grouped[broker].uniqueStocks.add(h.symbol);
      
      // Count by status (support both old 'pledged' field and new 'status' field)
      const status = h.status || (h.pledged ? 'pledged' : 'free');
      if (status === 'pledged') {
        grouped[broker].pledgedCount++;
      } else if (status === 'unlisted') {
        grouped[broker].unlistedCount++;
      } else {
        grouped[broker].freeCount++;
      }
    });

    // Convert sets to counts
    Object.values(grouped).forEach(g => {
      g.memberCount = g.members.size;
      g.stockCount = g.uniqueStocks.size;
      g.holdingCount = g.holdings.length;
      g.gainLoss = g.totalCurrentValue - g.totalInvested;
      g.gainLossPercent = g.totalInvested > 0 ? ((g.totalCurrentValue - g.totalInvested) / g.totalInvested) * 100 : 0;
    });

    return Object.values(grouped);
  }, [holdings]);

  // Get unique brokers for filter dropdown
  const uniqueBrokers = useMemo(() => {
    return [...new Set(holdings.map(h => h.bank_name || 'Unassigned'))].sort();
  }, [holdings]);

  // Get unique members for filter dropdown
  const uniqueMembers = useMemo(() => {
    const memberIds = [...new Set(holdings.map(h => h.member_id))];
    return memberIds.map(id => ({
      id,
      name: getMemberName(id)
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [holdings, members]);

  // Check if any filters are active
  const hasActiveFilters = filters.broker || filters.memberName || filters.search;

  // Filter and sort broker data
  const filteredAndSortedBrokers = useMemo(() => {
    let result = [...brokerData];
    
    // Apply filters
    if (filters.broker) {
      result = result.filter(b => b.broker === filters.broker);
    }
    if (filters.memberName) {
      result = result.filter(b => 
        b.holdings.some(h => h.member_id === filters.memberName)
      );
    }
    if (filters.search) {
      const search = filters.search.toLowerCase();
      result = result.filter(b => {
        const matchesBroker = b.broker.toLowerCase().includes(search);
        const matchesStock = b.holdings.some(h => h.symbol.toLowerCase().includes(search));
        return matchesBroker || matchesStock;
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'broker':
          aVal = a.broker || '';
          bVal = b.broker || '';
          return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'holdings':
          aVal = a.holdingCount;
          bVal = b.holdingCount;
          break;
        case 'value':
          aVal = a.totalCurrentValue;
          bVal = b.totalCurrentValue;
          break;
        case 'gainLoss':
          aVal = a.gainLossPercent;
          bVal = b.gainLossPercent;
          break;
        default:
          aVal = a.broker || '';
          bVal = b.broker || '';
          return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [brokerData, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setFilters({ broker: '', memberName: '', search: '' });
  };

  const toggleExpand = (broker) => {
    setExpandedBroker(expandedBroker === broker ? null : broker);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    if (Math.abs(amount) >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (Math.abs(amount) >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredAndSortedBrokers.reduce((acc, b) => ({
      holdings: acc.holdings + b.holdingCount,
      invested: acc.invested + b.totalInvested,
      currentValue: acc.currentValue + b.totalCurrentValue,
      stocks: acc.stocks + b.stockCount
    }), { holdings: 0, invested: 0, currentValue: 0, stocks: 0 });
  }, [filteredAndSortedBrokers]);

  const totalGainLoss = totals.currentValue - totals.invested;
  const totalGainLossPercent = totals.invested > 0 ? (totalGainLoss / totals.invested) * 100 : 0;

  const renderSortHeader = (key, label) => (
    <th 
      className="text-left py-3 px-4 font-semibold text-sm cursor-pointer hover:bg-gray-100"
      onClick={() => handleSort(key)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortConfig.key === key ? (
          <span className="text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
        ) : (
          <ArrowUpDown size={12} className="text-gray-400" />
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="text-center py-20">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div data-testid="demat-accounts-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2 flex items-center gap-3">
              <Briefcase className="text-primary" size={32} />
              Demat Holdings
            </h1>
            <p className="text-gray-600">View your holdings across different brokers and demat accounts</p>
          </div>
          <Button onClick={fetchData} variant="outline" className="flex items-center gap-2">
            <TrendingUp size={16} />
            Refresh Data
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Brokers</p>
                <p className="text-2xl font-bold">{uniqueBrokers.filter(b => b !== 'Unassigned').length}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CreditCard className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Holdings</p>
                <p className="text-2xl font-bold">{totals.holdings.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Portfolio Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.currentValue)}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-lg ${totalGainLoss >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {totalGainLoss >= 0 ? (
                  <TrendingUp className="text-green-600" size={24} />
                ) : (
                  <TrendingDown className="text-red-600" size={24} />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Overall P&L</p>
                <p className={`text-2xl font-bold ${totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
                </p>
                <p className={`text-xs ${totalGainLoss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {totalGainLoss >= 0 ? '+' : ''}{totalGainLossPercent.toFixed(2)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="card mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className={`flex items-center gap-2 ${hasActiveFilters ? 'border-primary text-primary' : ''}`}
              >
                <Filter size={16} />
                Filters {hasActiveFilters && '•'}
              </Button>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-primary hover:underline flex items-center gap-1">
                  <X size={14} />
                  Clear All
                </button>
              )}
            </div>
            <div className="text-sm text-gray-600">
              Showing {filteredAndSortedBrokers.length} of {brokerData.length} brokers
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Broker</label>
                <select
                  className="input-field w-full text-sm"
                  value={filters.broker}
                  onChange={(e) => setFilters({ ...filters, broker: e.target.value })}
                >
                  <option value="">All Brokers</option>
                  {uniqueBrokers.map(broker => (
                    <option key={broker} value={broker}>{broker}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Member</label>
                <select
                  className="input-field w-full text-sm"
                  value={filters.memberName}
                  onChange={(e) => setFilters({ ...filters, memberName: e.target.value })}
                >
                  <option value="">All Members</option>
                  {uniqueMembers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <input
                  type="text"
                  className="input-field w-full text-sm"
                  placeholder="Search by broker or stock..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Broker Holdings Table */}
        <div className="card">
          {filteredAndSortedBrokers.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">
                {holdings.length === 0 
                  ? 'No holdings found. Upload your portfolio data to get started.'
                  : 'No brokers match your filters.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="w-10"></th>
                    {renderSortHeader('broker', 'Broker / DP')}
                    <th className="text-left py-3 px-4 font-semibold text-sm">Members</th>
                    {renderSortHeader('holdings', 'Holdings')}
                    <th className="text-left py-3 px-4 font-semibold text-sm">Unique Stocks</th>
                    {renderSortHeader('value', 'Current Value')}
                    {renderSortHeader('gainLoss', 'P&L')}
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedBrokers.map((brokerInfo) => {
                    const isExpanded = expandedBroker === brokerInfo.broker;
                    
                    return (
                      <React.Fragment key={brokerInfo.broker}>
                        <tr 
                          className={`border-b hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-gray-50' : ''}`}
                          onClick={() => toggleExpand(brokerInfo.broker)}
                        >
                          <td className="py-3 px-2 text-center">
                            {isExpanded ? (
                              <ChevronUp size={18} className="text-gray-500" />
                            ) : (
                              <ChevronDown size={18} className="text-gray-400" />
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Building2 size={16} className="text-gray-400" />
                              <span className="font-semibold">{brokerInfo.broker}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="badge badge-info text-xs">{brokerInfo.memberCount}</span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{brokerInfo.holdingCount}</span>
                              <span className="text-xs text-gray-500">
                                ({brokerInfo.freeCount} free, {brokerInfo.pledgedCount} pledged{brokerInfo.unlistedCount > 0 ? `, ${brokerInfo.unlistedCount} unlisted` : ''})
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">{brokerInfo.stockCount}</td>
                          <td className="py-3 px-4 font-semibold">
                            {formatCurrency(brokerInfo.totalCurrentValue)}
                          </td>
                          <td className="py-3 px-4">
                            <div className={`font-semibold ${brokerInfo.gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {brokerInfo.gainLoss >= 0 ? '+' : ''}{formatCurrency(brokerInfo.gainLoss)}
                            </div>
                            <div className={`text-xs ${brokerInfo.gainLoss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {brokerInfo.gainLoss >= 0 ? '+' : ''}{brokerInfo.gainLossPercent.toFixed(2)}%
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Holdings Details */}
                        {isExpanded && (
                          <tr className="bg-gray-50">
                            <td colSpan={7} className="py-0">
                              <div className="px-6 py-4 border-t border-gray-200">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                  <FileText size={16} />
                                  Holdings at {brokerInfo.broker} ({brokerInfo.holdingCount} records)
                                </h4>
                                
                                <div className="bg-white rounded-lg border overflow-hidden max-h-80 overflow-y-auto">
                                  <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-gray-100">
                                      <tr className="border-b">
                                        <th className="text-left py-2 px-3 font-medium">Symbol</th>
                                        <th className="text-left py-2 px-3 font-medium">Member</th>
                                        <th className="text-right py-2 px-3 font-medium">Qty</th>
                                        <th className="text-right py-2 px-3 font-medium">Avg Price</th>
                                        <th className="text-right py-2 px-3 font-medium">Current Price</th>
                                        <th className="text-right py-2 px-3 font-medium">Value</th>
                                        <th className="text-center py-2 px-3 font-medium">Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {brokerInfo.holdings
                                        .sort((a, b) => {
                                          const aVal = (a.quantity || 0) * (a.custom_fields?.current_price || a.purchase_price || 0);
                                          const bVal = (b.quantity || 0) * (b.custom_fields?.current_price || b.purchase_price || 0);
                                          return bVal - aVal;
                                        })
                                        .map(holding => {
                                          const currentPrice = holding.custom_fields?.current_price || holding.purchase_price || 0;
                                          const value = (holding.quantity || 0) * currentPrice;
                                          const invested = (holding.quantity || 0) * (holding.purchase_price || 0);
                                          const gainLoss = value - invested;
                                          
                                          return (
                                            <tr key={holding.id} className="border-b last:border-b-0 hover:bg-gray-50">
                                              <td className="py-2 px-3">
                                                <span className="font-medium">{holding.display_name || holding.symbol}</span>
                                                {holding.display_name && holding.display_name !== holding.symbol && (
                                                  <span className="text-xs text-gray-400 block">{holding.symbol}</span>
                                                )}
                                              </td>
                                              <td className="py-2 px-3 text-gray-600">
                                                {getMemberName(holding.member_id)}
                                              </td>
                                              <td className="py-2 px-3 text-right">{(holding.quantity || 0).toLocaleString()}</td>
                                              <td className="py-2 px-3 text-right">
                                                ₹{(holding.purchase_price || 0).toFixed(2)}
                                              </td>
                                              <td className="py-2 px-3 text-right">
                                                ₹{currentPrice.toFixed(2)}
                                              </td>
                                              <td className="py-2 px-3 text-right">
                                                <div className="font-medium">{formatCurrency(value)}</div>
                                                <div className={`text-xs ${gainLoss >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                  {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)}
                                                </div>
                                              </td>
                                              <td className="py-2 px-3 text-center">
                                                {(() => {
                                                  const status = holding.status || (holding.pledged ? 'pledged' : 'free');
                                                  if (status === 'pledged') {
                                                    return <span className="badge badge-warning text-xs">Pledged</span>;
                                                  } else if (status === 'unlisted') {
                                                    return <span className="badge badge-secondary text-xs bg-gray-200 text-gray-700">Unlisted</span>;
                                                  }
                                                  return <span className="badge badge-success text-xs">Free</span>;
                                                })()}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                    <tfoot className="sticky bottom-0 bg-gray-100">
                                      <tr className="font-semibold">
                                        <td className="py-2 px-3">Total</td>
                                        <td className="py-2 px-3"></td>
                                        <td className="py-2 px-3 text-right">
                                          {brokerInfo.holdings.reduce((sum, h) => sum + (h.quantity || 0), 0).toLocaleString()}
                                        </td>
                                        <td className="py-2 px-3"></td>
                                        <td className="py-2 px-3"></td>
                                        <td className="py-2 px-3 text-right">
                                          {formatCurrency(brokerInfo.totalCurrentValue)}
                                        </td>
                                        <td className="py-2 px-3"></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t bg-gray-100 font-semibold">
                    <td className="py-3 px-4" colSpan={3}>Grand Total</td>
                    <td className="py-3 px-4">{totals.holdings.toLocaleString()}</td>
                    <td className="py-3 px-4">{totals.stocks}</td>
                    <td className="py-3 px-4">{formatCurrency(totals.currentValue)}</td>
                    <td className="py-3 px-4">
                      <span className={totalGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {totalGainLoss >= 0 ? '+' : ''}{formatCurrency(totalGainLoss)}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DematAccounts;
